import "gantt-task-react/dist/index.css";
import { GanttDto } from "@pma/types";
import React, { useEffect, useState } from "react";
import { Gantt, Task, ViewMode } from "gantt-task-react";
import useTranslator from "@hooks/use-translator";
import { formatGanttTasks } from "@pma/utils/helpers";
import { useModal } from "@stores/modal-store";
import { Button } from "@components/common/button";
import { PlusIcon } from "lucide-react";
import { ConfirmDialog, ViewSwitcher } from "./gannt-chart-view-switcher";

interface Props {
  tasks: GanttDto[] | [];

  revalidate?: CallableFunction;
}
const GanttChart = ({ tasks, revalidate }: Props) => {
  const [view, setView] = useState<ViewMode>(ViewMode.Day);
  const [ganttTasks, setTasks] = React.useState<Task[] | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTask, setActiveTask] = useState({});

  const [isChecked, setIsChecked] = useState(true);
  const { locale, translate } = useTranslator();

  const modal = useModal();

  const isHebrew = locale === "he";

  let columnWidth = 60;
  if (view === ViewMode.Month) {
    columnWidth = 300;
  } else if (view === ViewMode.Week) {
    columnWidth = 250;
  }

  useEffect(() => {
    const gtask = tasks?.length > 0 ? formatGanttTasks(tasks) : [];
    setTasks(gtask);
  }, [tasks]);

  useEffect(() => {
    const hebrewTranslations: Record<string, string> = {
      "Name": "שם",
      "From": "מאת",
      "To": "אל"
    };

    const englishTranslations: Record<string, string> = {
      "שם": "Name",
      "מאת": "From",
      "אל": "To"
    };

    const translateHeaders = () => {
      document.querySelectorAll("._WuQ0f").forEach((el) => {
        const text = el.textContent?.trim();

        if (isHebrew) {
          // Translate to Hebrew
          if (text && hebrewTranslations[text]) {
            el.innerHTML = `&nbsp;&nbsp;${hebrewTranslations[text]}&nbsp;&nbsp;`;
          }
        } else {
          // Translate back to English
          if (text && englishTranslations[text]) {
            el.innerHTML = `&nbsp;&nbsp;${englishTranslations[text]}&nbsp;&nbsp;`;
          }
        }
      });
    };

    // Try immediate translation
    translateHeaders();

    // Also translate after a short delay to catch dynamically rendered content
    const timeoutId = setTimeout(translateHeaders, 100);

    // Set up observer for dynamic content changes
    const observer = new MutationObserver((mutations) => {
      let shouldTranslate = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldTranslate = true;
        }
      });
      if (shouldTranslate) {
        setTimeout(translateHeaders, 50);
      }
    });

    // Start observing
    const ganttContainer = document.querySelector('.gantt-container');
    if (ganttContainer) {
      observer.observe(ganttContainer, {
        childList: true,
        subtree: true
      });
    }

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [isHebrew, ganttTasks]); // Re-run when language or tasks change

  return (
    <div>
      <ViewSwitcher
        onViewModeChange={(viewMode: ViewMode) => setView(viewMode)}
        onViewListChange={setIsChecked}
        isChecked={isChecked}
      />

      {ganttTasks && ganttTasks.length > 0 ? (
        <Gantt
          tasks={ganttTasks}
          viewMode={view}
          onDateChange={(task) => {
            setActiveTask(task)
            setIsOpen(true)
          }}
          //   onDelete={handleTaskDelete}
          //   onProgressChange={handleProgressChange}
          //   onDoubleClick={handleDblClick}
          //   onSelect={handleSelect}
          //   onExpanderClick={handleExpanderClick}
          listCellWidth={isChecked ? "155px" : ""}
          columnWidth={columnWidth}
          fontSize='12px'
          projectProgressColor='#008000'
          arrowColor='#008000'
          todayColor='#bdf2a577'
          locale={locale}
        />
      ) : (
        <div className='flex h-44 w-full items-center justify-center'>
          <Button
            onClick={() => {
              modal.onOpen()
              modal.setContext("task")
            }}
            variant={"outline"}
            className='border-1 flex h-[60px] w-[350px] min-w-[350px] items-center gap-2 rounded-lg border-primary bg-primary/20 p-4 text-primary ring-slate-300 hover:ring-2'
          >
            <PlusIcon />
            {translate("Add Task")}{" "}
          </Button>
        </div>
      )}

      <ConfirmDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        task={activeTask}
        setTasks={setTasks}
        ganttTasks={ganttTasks}
      />
    </div>
  )
};

export default GanttChart;
